import React, { useState, useEffect } from 'react';
import {
  Save, RefreshCw, Shield, CreditCard,
  Server, Lock, CheckCircle2, Download,
  Radio, Activity, KeyRound, Eye, EyeOff,
  AlertCircle, Mail
} from 'lucide-react';
import {
  fetchSystemSettings,
  updateSystemSettings,
  testEdgeConnection,
  downloadJSON,
  SystemSettings,
  DEFAULT_SETTINGS,
  DEFAULT_API_HOST
} from '../utils/api';
import {
  getAdminEmail,
  setAdminEmail,
  changeAdminPassword
} from '../utils/auth';

export const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'general' | 'kyc' | 'pricing' | 'security'>('general');
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Admin Account & Password State
  const [adminEmailInput, setAdminEmailInput] = useState(() => getAdminEmail());
  const [adminCurrentPass, setAdminCurrentPass] = useState('');
  const [adminNewPass, setAdminNewPass] = useState('');
  const [adminConfirmPass, setAdminConfirmPass] = useState('');
  const [showAdminCurrentPass, setShowAdminCurrentPass] = useState(false);
  const [showAdminNewPass, setShowAdminNewPass] = useState(false);
  const [adminPassError, setAdminPassError] = useState('');
  const [adminPassSuccess, setAdminPassSuccess] = useState('');
  const [adminPassSaving, setAdminPassSaving] = useState(false);

  // Edge connection test state
  const [testingPing, setTestingPing] = useState(false);
  const [pingResult, setPingResult] = useState<{ ok: boolean; status: number; latencyMs: number; message: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const data = await fetchSystemSettings();
    setSettings(data);
    setLoading(false);
  };

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateSystemSettings(settings);
      setSettings(res.settings);
      showToast('⚡ System configuration saved & synced with Edge worker!');
    } catch (e) {
      showToast('❌ Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all settings to system defaults?')) {
      setSettings(DEFAULT_SETTINGS);
      updateSystemSettings(DEFAULT_SETTINGS);
      showToast('Settings reset to platform defaults.');
    }
  };

  const handleSaveAdminEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminEmailInput.trim()) {
      showToast('❌ Admin email cannot be empty');
      return;
    }
    setAdminEmail(adminEmailInput);
    showToast('✓ Admin email updated successfully');
  };

  const handleChangeAdminPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdminPassError('');
    setAdminPassSuccess('');

    if (!adminCurrentPass || !adminNewPass || !adminConfirmPass) {
      setAdminPassError('Please fill in all password fields.');
      return;
    }
    if (adminNewPass.length < 6) {
      setAdminPassError('New password must be at least 6 characters long.');
      return;
    }
    if (adminNewPass !== adminConfirmPass) {
      setAdminPassError('New passwords do not match. Please verify.');
      return;
    }

    setAdminPassSaving(true);
    await new Promise((r) => setTimeout(r, 600));

    const res = changeAdminPassword(adminCurrentPass, adminNewPass);
    setAdminPassSaving(false);

    if (res.success) {
      setAdminPassSuccess(res.message);
      setAdminCurrentPass('');
      setAdminNewPass('');
      setAdminConfirmPass('');
      showToast('✓ Admin password changed successfully');
    } else {
      setAdminPassError(res.message);
    }
  };

  const handleExportConfig = () => {
    downloadJSON(`amoura-system-settings-${new Date().toISOString().slice(0, 10)}.json`, {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      settings,
    });
    showToast('Configuration exported as JSON');
  };

  const handleTestPing = async () => {
    setTestingPing(true);
    const res = await testEdgeConnection();
    setPingResult(res);
    setTestingPing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3 text-zinc-400">
          <RefreshCw size={20} className="animate-spin text-pink-500" />
          <span className="text-sm font-semibold">Loading system configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* ── Toast Notification ── */}
      {toastMessage && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#141428] border border-emerald-500/40 text-white shadow-2xl shadow-emerald-500/10">
          <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
          <p className="text-xs font-bold">{toastMessage}</p>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-['Outfit']">System Settings</h1>
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-pink-500/20 text-pink-300 border border-pink-500/30 flex items-center gap-1.5 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" />
              Edge Synced
            </span>
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            Configure Cloudflare Worker bindings, KYC validation gates, SquadCo billing tiers & security policies
          </p>
        </div>

        {/* Action CTAs */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            onClick={handleExportConfig}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-zinc-300 hover:text-white hover:border-zinc-500 transition-colors cursor-pointer"
            title="Download settings JSON"
          >
            <Download size={14} /> Export Config
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-zinc-400 hover:text-red-400 hover:border-red-500/40 transition-colors cursor-pointer"
            title="Reset to factory settings"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white shadow-lg shadow-pink-500/25 transition-all cursor-pointer disabled:opacity-50"
          >
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            <span>{saving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </div>

      {/* ── Settings Navigation Tabs ── */}
      <div className="flex border-b border-[#1e1e3a] gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'general'
              ? 'border-pink-500 text-white bg-pink-500/5'
              : 'border-transparent text-zinc-400 hover:text-white'
          }`}
        >
          <Server size={16} /> Edge Engine & General
        </button>
        <button
          onClick={() => setActiveTab('kyc')}
          className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'kyc'
              ? 'border-pink-500 text-white bg-pink-500/5'
              : 'border-transparent text-zinc-400 hover:text-white'
          }`}
        >
          <Shield size={16} /> KYC & Trust Scores
        </button>
        <button
          onClick={() => setActiveTab('pricing')}
          className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'pricing'
              ? 'border-pink-500 text-white bg-pink-500/5'
              : 'border-transparent text-zinc-400 hover:text-white'
          }`}
        >
          <CreditCard size={16} /> Pricing & SquadCo (₦)
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'security'
              ? 'border-pink-500 text-white bg-pink-500/5'
              : 'border-transparent text-zinc-400 hover:text-white'
          }`}
        >
          <Lock size={16} /> Zero Trust & E2EE
        </button>
      </div>

      {/* ── Tab Content ── */}

      {/* TAB 1: General & Edge Engine */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Edge Health Diagnostic Card */}
          <div className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] relative overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Activity size={18} className="text-emerald-400" />
                  <h3 className="text-sm font-bold text-white">Cloudflare Edge Diagnostics</h3>
                </div>
                <p className="text-xs text-zinc-400 mt-1">
                  Active connection to Cloudflare Workers, D1 Database (<code className="text-pink-400">ca600841</code>), and KV namespaces
                </p>
              </div>
              <button
                onClick={handleTestPing}
                disabled={testingPing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-[#141428] border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors self-start sm:self-auto cursor-pointer"
              >
                <Radio size={14} className={testingPing ? 'animate-pulse' : ''} />
                <span>{testingPing ? 'Testing latency...' : 'Test Edge Connection'}</span>
              </button>
            </div>

            {pingResult && (
              <div className="mt-4 p-3.5 rounded-xl bg-[#141428] border border-[#1e1e3a] flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${pingResult.ok ? 'bg-emerald-400 animate-pulse' : 'bg-red-500'}`} />
                  <span className="font-bold text-white">Status: {pingResult.ok ? 'Edge Online' : 'Edge Unreachable'}</span>
                  <span className="text-zinc-500">({pingResult.message})</span>
                </div>
                <div className="flex items-center gap-4 text-zinc-400">
                  <span>Latency: <strong className="text-emerald-400">{pingResult.latencyMs} ms</strong></span>
                  <span>HTTP Status: <strong className="text-white">{pingResult.status}</strong></span>
                </div>
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="p-6 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">Platform Name</label>
                <input
                  type="text"
                  value={settings.platformName}
                  onChange={(e) => setSettings({ ...settings, platformName: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">Ops / Support Email</label>
                <input
                  type="email"
                  value={settings.supportEmail}
                  onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">Cloudflare Worker API Host</label>
                <input
                  type="text"
                  value={settings.workerApiUrl}
                  onChange={(e) => setSettings({ ...settings, workerApiUrl: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white font-mono focus:outline-none focus:border-pink-500"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Default: {DEFAULT_API_HOST}</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">Maintenance Mode</label>
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="maint_mode"
                    checked={settings.maintenanceMode}
                    onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                    className="w-4 h-4 rounded text-pink-600 bg-[#141428] border-zinc-700"
                  />
                  <label htmlFor="maint_mode" className="text-xs text-zinc-300">
                    Suspend mobile discovery & display maintenance window
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: KYC & Trust Scores */}
      {activeTab === 'kyc' && (
        <div className="space-y-6">
          <div className="p-6 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] space-y-6">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Shield size={16} className="text-amber-400" />
              Verification Rules & Discovery Gates
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">
                  Minimum Approved Photos for Discovery Gate
                </label>
                <input
                  type="number"
                  min={1}
                  max={6}
                  value={settings.minPhotosRequired}
                  onChange={(e) => setSettings({ ...settings, minPhotosRequired: parseInt(e.target.value) || 3 })}
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Users are hidden from discovery until this threshold is verified.</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">
                  Selfie Liveness Sensitivity
                </label>
                <select
                  value={settings.livenessSensitivity}
                  onChange={(e) => setSettings({ ...settings, livenessSensitivity: e.target.value as any })}
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                >
                  <option value="low">Low (Standard face match)</option>
                  <option value="medium">Medium (Blink & head tilt check)</option>
                  <option value="high">High (Strict biometric micro-movement)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">Dojah NIN Auto-Approval</label>
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="auto_nin"
                    checked={settings.autoVerifyNIN}
                    onChange={(e) => setSettings({ ...settings, autoVerifyNIN: e.target.checked })}
                    className="w-4 h-4 rounded text-pink-600 bg-[#141428] border-zinc-700"
                  />
                  <label htmlFor="auto_nin" className="text-xs text-zinc-300">
                    Instantly approve KYC Level 1 when Dojah NIN returns 100% confidence
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">Dojah BVN Auto-Approval</label>
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="auto_bvn"
                    checked={settings.autoVerifyBVN}
                    onChange={(e) => setSettings({ ...settings, autoVerifyBVN: e.target.checked })}
                    className="w-4 h-4 rounded text-pink-600 bg-[#141428] border-zinc-700"
                  />
                  <label htmlFor="auto_bvn" className="text-xs text-zinc-300">
                    Auto-grant Trust Score bonus (+25) upon valid BVN verification
                  </label>
                </div>
              </div>
            </div>

            {/* Trust Score Algorithm Breakdown */}
            <div className="mt-6 pt-6 border-t border-[#1e1e3a]">
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">
                Trust Score Classification
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="p-3.5 rounded-xl bg-[#141428] border border-[#1e1e3a]">
                  <p className="font-bold text-zinc-400">0 - 30 Score</p>
                  <p className="text-red-400 font-semibold mt-1">Unverified (Hidden)</p>
                  <p className="text-[11px] text-zinc-500 mt-1">Restricted from matching</p>
                </div>
                <div className="p-3.5 rounded-xl bg-[#141428] border border-amber-500/20">
                  <p className="font-bold text-zinc-400">31 - 55 Score</p>
                  <p className="text-amber-400 font-semibold mt-1">Basic 🟡</p>
                  <p className="text-[11px] text-zinc-500 mt-1">Phone OTP verified</p>
                </div>
                <div className="p-3.5 rounded-xl bg-[#141428] border border-emerald-500/20">
                  <p className="font-bold text-zinc-400">56 - 80 Score</p>
                  <p className="text-emerald-400 font-semibold mt-1">Trusted 🟢</p>
                  <p className="text-[11px] text-zinc-500 mt-1">NIN + 3 Photos verified</p>
                </div>
                <div className="p-3.5 rounded-xl bg-[#141428] border border-cyan-500/20">
                  <p className="font-bold text-zinc-400">81 - 100 Score</p>
                  <p className="text-cyan-400 font-semibold mt-1">Elite 💎</p>
                  <p className="text-[11px] text-zinc-500 mt-1">BVN + Biometric verified</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: Pricing & SquadCo */}
      {activeTab === 'pricing' && (
        <div className="space-y-6">
          <div className="p-6 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] space-y-6">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <CreditCard size={16} className="text-emerald-400" />
              Naira Pricing Plans & SquadCo Webhook Config
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">Instant Match Ticket (₦)</label>
                <input
                  type="number"
                  step="100"
                  value={settings.matchTicketPriceNGN}
                  onChange={(e) => setSettings({ ...settings, matchTicketPriceNGN: parseInt(e.target.value) || 500 })}
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white font-mono focus:outline-none focus:border-pink-500"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Single-use mutual matching ticket</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">2-Hour Chat Session (₦)</label>
                <input
                  type="number"
                  step="100"
                  value={settings.chat2HrPriceNGN}
                  onChange={(e) => setSettings({ ...settings, chat2HrPriceNGN: parseInt(e.target.value) || 500 })}
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white font-mono focus:outline-none focus:border-pink-500"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Continuous encrypted chat window</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-300 mb-2">Elite Weekly Pass (₦)</label>
                <input
                  type="number"
                  step="500"
                  value={settings.eliteWeeklyPriceNGN}
                  onChange={(e) => setSettings({ ...settings, eliteWeeklyPriceNGN: parseInt(e.target.value) || 5000 })}
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white font-mono focus:outline-none focus:border-pink-500"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Unlimited 7-day chat & matching</p>
              </div>
            </div>

            <div className="pt-4 border-t border-[#1e1e3a] grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="free_chat"
                  checked={settings.allowFreeChat}
                  onChange={(e) => setSettings({ ...settings, allowFreeChat: e.target.checked })}
                  className="w-4 h-4 rounded text-pink-600 bg-[#141428] border-zinc-700"
                />
                <div>
                  <label htmlFor="free_chat" className="text-xs font-bold text-zinc-300">Allow Free Initial Chat Message</label>
                  <p className="text-[11px] text-zinc-500">Enable users to send 1 free opener message before requiring a pass</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="auto_refund"
                  checked={settings.autoRefundDisputes}
                  onChange={(e) => setSettings({ ...settings, autoRefundDisputes: e.target.checked })}
                  className="w-4 h-4 rounded text-pink-600 bg-[#141428] border-zinc-700"
                />
                <div>
                  <label htmlFor="auto_refund" className="text-xs font-bold text-zinc-300">Auto-Approve USSD Session Disputes</label>
                  <p className="text-[11px] text-zinc-500">Refund unfulfilled ₦500 tickets automatically within 24 hours</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: Zero Trust & Security */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="p-6 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] space-y-6">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Lock size={16} className="text-purple-400" />
              Cloudflare Zero Trust & Cryptographic Integrity
            </h3>

            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-[#141428] border border-[#1e1e3a] flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-white">Cloudflare Access JWT Validation</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Enforce <code className="text-pink-400">Cf-Access-Jwt-Assertion</code> for all admin requests</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.zeroTrustRequired}
                  onChange={(e) => setSettings({ ...settings, zeroTrustRequired: e.target.checked })}
                  className="w-4 h-4 rounded text-pink-600 bg-[#141428] border-zinc-700"
                />
              </div>

              <div className="p-4 rounded-xl bg-[#141428] border border-[#1e1e3a] flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-white">E2EE Signal Protocol (ECDH P-256 + AES-256-GCM)</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Client prekey bundle storage via Cloudflare KV with zero server decryption capability</p>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  Enforced
                </span>
              </div>

              <div className="p-4 rounded-xl bg-[#141428] border border-[#1e1e3a] flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-white">Bot Protection: Cloudflare Turnstile</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Required on mutual like endpoints and registration flows</p>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  Active
                </span>
              </div>
            </div>
          </div>

          {/* ── Admin Credentials & Password Management Card ── */}
          <div className="p-6 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <KeyRound size={16} className="text-pink-400" />
                  Admin Account Credentials & Password
                </h3>
                <p className="text-xs text-zinc-400 mt-1">
                  Manage backoffice access credentials. Passwords are encrypted and persisted securely.
                </p>
              </div>
            </div>

            {/* Admin Email Update */}
            <form onSubmit={handleSaveAdminEmail} className="p-4 rounded-xl bg-[#141428] border border-[#1e1e3a]">
              <label className="block text-xs font-semibold text-zinc-300 mb-2">Administrator Login Email</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type="email"
                    value={adminEmailInput}
                    onChange={(e) => setAdminEmailInput(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-xs rounded-xl bg-[#0d0d1a] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                    placeholder="admin@amoura.app"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 text-xs font-bold rounded-xl bg-[#1e1e3a] hover:bg-zinc-700 text-white transition-colors cursor-pointer self-start sm:self-auto"
                >
                  Save Email
                </button>
              </div>
            </form>

            {/* Change Password Form */}
            <form onSubmit={handleChangeAdminPassword} className="p-4 rounded-xl bg-[#141428] border border-[#1e1e3a] space-y-4">
              <h4 className="text-xs font-bold text-zinc-200">Change Admin Password</h4>

              {adminPassError && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl text-xs bg-red-950/40 border border-red-500/30 text-red-300">
                  <AlertCircle size={15} className="mt-0.5 shrink-0" />
                  <span>{adminPassError}</span>
                </div>
              )}

              {adminPassSuccess && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl text-xs bg-emerald-950/40 border border-emerald-500/30 text-emerald-300">
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
                  <span>{adminPassSuccess}</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-400 mb-1.5">Current Password</label>
                  <div className="relative">
                    <input
                      type={showAdminCurrentPass ? 'text' : 'password'}
                      value={adminCurrentPass}
                      onChange={(e) => setAdminCurrentPass(e.target.value)}
                      placeholder="Current password"
                      className="w-full pl-3 pr-9 py-2 text-xs rounded-xl bg-[#0d0d1a] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowAdminCurrentPass(!showAdminCurrentPass)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 cursor-pointer"
                      tabIndex={-1}
                    >
                      {showAdminCurrentPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-zinc-400 mb-1.5">New Password</label>
                  <div className="relative">
                    <input
                      type={showAdminNewPass ? 'text' : 'password'}
                      value={adminNewPass}
                      onChange={(e) => setAdminNewPass(e.target.value)}
                      placeholder="Min 6 characters"
                      className="w-full pl-3 pr-9 py-2 text-xs rounded-xl bg-[#0d0d1a] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowAdminNewPass(!showAdminNewPass)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 cursor-pointer"
                      tabIndex={-1}
                    >
                      {showAdminNewPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-zinc-400 mb-1.5">Confirm New Password</label>
                  <input
                    type={showAdminNewPass ? 'text' : 'password'}
                    value={adminConfirmPass}
                    onChange={(e) => setAdminConfirmPass(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full px-3 py-2 text-xs rounded-xl bg-[#0d0d1a] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={adminPassSaving}
                  className="px-5 py-2 text-xs font-bold rounded-xl bg-gradient-to-r from-red-600 to-pink-600 hover:opacity-90 text-white transition-opacity flex items-center gap-2 cursor-pointer shadow-lg shadow-pink-600/20"
                >
                  {adminPassSaving ? (
                    <>
                      <RefreshCw size={13} className="animate-spin text-white" />
                      Updating Password...
                    </>
                  ) : (
                    'Update Admin Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
